import config from '@/utils/ApiUtils'
import { fetchWithToken } from '../utils/Keycloak'

// Une réponse en erreur porte son message dans le corps ({ error } ou { message }) :
// il devient le message de l'exception au lieu d'être rendu comme un résultat.
const errorMessage = (status, text) => Promise.resolve(text)
  .then(JSON.parse)
  .then(body => body.error || body.message)
  .catch(() => '')
  .then(message => message || `Erreur ${status}`)

const readJson = response => response.ok
  ? response.json()
  : response.text().then(text => errorMessage(response.status, text)).then(message => { throw new Error(message) })

export default {
  sendMail(expediteur, destinataires, contenu, pieceJointe = [], cc = [], cci = []) {
    return fetchWithToken(config().mailSenderBackend + 'send-mail', {
      method: 'POST',
      body: JSON.stringify({ expediteur, destinataires, contenu, pieceJointe, cc, cci })
    }).then(readJson)
  },

  /**
   * Envoie un mail via un template enregistré en base (table `template`).
   * @param {Object} payload - { type, mail, subject, ...variables du template }
   * @returns {Promise<{ id: string }>} - id = sid Mailjet pour le suivi du statut
   */
  sendTemplatedMail(payload) {
    return fetchWithToken(config().mailSenderBackend + 'mail', {
      method: 'POST',
      body: JSON.stringify(payload)
    }).then(readJson)
  },

  // --- Boîte de réception (Gmail de l'utilisateur de la session) ---

  getInboxStatus() {
    return fetchWithToken(config().mailSenderBackend + 'inbox/status', {
      method: 'GET'
    }).then(readJson)
  },

  // folder : INBOX (réception), SENT (envoyés) ou TRASH (corbeille)
  getInbox({ folder = 'INBOX', limit = 25, pageToken = '', search = '' } = {}) {
    const params = new URLSearchParams({ folder, limit })
    if (pageToken) params.append('pageToken', pageToken)
    if (search) params.append('search', search)
    return fetchWithToken(config().mailSenderBackend + `inbox?${params.toString()}`, {
      method: 'GET'
    }).then(readJson)
  },

  getInboxMessage(messageId) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}`, {
      method: 'GET'
    }).then(readJson)
  },

  // --- Actions sur la boîte de réception ---

  getInboxAttachment(messageId, attachmentId) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}/attachment/${attachmentId}`, {
      method: 'GET'
    }).then(readJson)
  },

  setInboxMessageRead(messageId, read) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}/read`, {
      method: 'POST',
      body: JSON.stringify({ read })
    }).then(readJson)
  },

  trashInboxMessage(messageId) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}/trash`, {
      method: 'POST'
    }).then(readJson)
  },

  untrashInboxMessage(messageId) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}/untrash`, {
      method: 'POST'
    }).then(readJson)
  },

  replyToInboxMessage(messageId, payload) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}/reply`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }).then(readJson)
  },

  forwardInboxMessage(messageId, payload) {
    return fetchWithToken(config().mailSenderBackend + `inbox/${messageId}/forward`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }).then(readJson)
  },

  getHistory({ limit = 50, offset = 0, search = '', recipients = '' } = {}) {
    const params = new URLSearchParams({ limit, offset })
    if (search) params.append('search', search)
    if (recipients) params.append('recipients', recipients)
    return fetchWithToken(config().mailSenderBackend + `mail/history?${params.toString()}`, {
      method: 'GET'
    }).then(readJson)
  },

  getMessageHistory(messageId) {
    return fetchWithToken(config().mailSenderBackend + `message-history/${messageId}`, {
      method: 'GET'
    }).then(readJson)
  },

  getMessageDetails(messageId) {
    return fetchWithToken(config().mailSenderBackend + `message/${messageId}`, {
      method: 'GET'
    }).then(readJson)
  },

  getMessageStatuses(sids) {
    return fetchWithToken(config().mailSenderBackend + 'messages/statuses', {
      method: 'POST',
      body: JSON.stringify({ sids })
    }).then(readJson)
  },

  getCustomTemplates() {
    return fetchWithToken(config().mailSenderBackend + 'template/custom', {
      method: 'GET'
    }).then(readJson)
  },

  saveCustomTemplate(template) {
    return fetchWithToken(config().mailSenderBackend + 'template/custom', {
      method: 'POST',
      body: JSON.stringify(template)
    }).then(readJson)
  },

  deleteCustomTemplate(templateId) {
    return fetchWithToken(config().mailSenderBackend + `template/custom/${templateId}`, {
      method: 'DELETE'
    }).then(readJson)
  },

  getRecipientFilters() {
    return fetchWithToken(config().mailSenderBackend + 'filter', {
      method: 'GET'
    }).then(readJson)
  },

  saveRecipientFilter(filter) {
    return fetchWithToken(config().mailSenderBackend + 'filter', {
      method: 'POST',
      body: JSON.stringify(filter)
    }).then(readJson)
  },

  deleteRecipientFilter(filterId) {
    return fetchWithToken(config().mailSenderBackend + `filter/${filterId}`, {
      method: 'DELETE'
    }).then(readJson)
  },
}
